import { loadJSData } from "./jsData";
import type { AnswerType, CollectFeedbackQuestionType, Language, TaskThreadItemType } from "./types";

export interface ISubmittedTaskWithThread {
    shortTitle: string;
    title?: string | null;
    submittedTaskVariant?: {
        variantTitle?: string | null;
        description?: string | null;
        hoursQuestions?: string | null;
    } | null;
    thread?: {
        type: TaskThreadItemType;
        content?: string | null;
        taskAnswers?: {
            answerType: AnswerType;
            questionContent: string | null;
            answerContent?: string | null;
            imageSelectionAnswers?: {
                answer: string;
                explanation: string | null;
            }[];
            taskLikertAnswers?: {
                statement: string;
                leftLabel: string;
                rightLabel: string;
            }[];
            checkboxAnswers?: {
                content: string;
            }[];
            impactTrackerAnswerSet?: {
                impactLikertAnswers: {
                    statement: string;
                    leftLabel: string;
                    rightLabel: string;
                }[];
            };
            taskAnswerFile?: {
                name: string;
            };
        }[];
    }[];
}

/**
 * Builds a context string for thread translation from a submitted task
 *
 * @param submittedTask The submitted task containing thread items
 * @returns A formatted context string with task and thread information
 */
export function buildThreadTranslationContext(submittedTask: ISubmittedTaskWithThread): string {
    const contextParts: string[] = [];

    // Add task information to context
    if (submittedTask?.shortTitle) {
        contextParts.push(`Short Title: ${submittedTask.shortTitle}`);
    }

    if (submittedTask?.title) {
        contextParts.push(`Title: ${submittedTask.title}`);
    }

    const variant = submittedTask?.submittedTaskVariant;

    if (variant?.variantTitle) {
        contextParts.push(`Variant Title: ${variant.variantTitle}`);
    }

    if (variant?.description) {
        contextParts.push(variant.description);
    }

    if (variant?.hoursQuestions) {
        contextParts.push(variant.hoursQuestions);
    }

    // Add thread items to context
    if (submittedTask?.thread) {
        for (const item of submittedTask.thread) {
            if (item.type === "feedback" && item.content) {
                contextParts.push("Feedback:");
                contextParts.push(item.content);
            } else if (item.type === "response" && item.content) {
                contextParts.push("Response:");
                contextParts.push(item.content);
            } else if (item.type === "answer" && item.taskAnswers) {
                for (const answer of item.taskAnswers) {
                    if (answer.questionContent) {
                        contextParts.push(`Question: ${answer.questionContent}`);
                    }
                    if (answer.answerType === "textbox") {
                        if (answer.answerContent) {
                            contextParts.push(answer.answerContent);
                        }
                    } else if (answer.answerType === "select-in-image") {
                        if (answer.imageSelectionAnswers) {
                            for (const imageSelectionAnswer of answer.imageSelectionAnswers) {
                                if (imageSelectionAnswer.answer) {
                                    contextParts.push(`Answer: ${imageSelectionAnswer.answer}`);
                                }
                                if (imageSelectionAnswer.explanation) {
                                    contextParts.push(`Explanation: ${imageSelectionAnswer.explanation}`);
                                }
                            }
                        }
                    } else if (answer.answerType === "likert" && answer.taskLikertAnswers) {
                        for (const likertAnswer of answer.taskLikertAnswers) {
                            contextParts.push(`Statement: ${likertAnswer.statement}`);
                            contextParts.push(`Left Label: ${likertAnswer.leftLabel}`);
                            contextParts.push(`Right Label: ${likertAnswer.rightLabel}`);
                        }
                    } else if (answer.answerType === "checkbox" && answer.checkboxAnswers) {
                        for (const checkboxAnswer of answer.checkboxAnswers) {
                            contextParts.push(`Checkbox option: ${checkboxAnswer.content}`);
                        }
                    } else if (answer.answerType === "impact-tracker" && answer.impactTrackerAnswerSet) {
                        if (answer.impactTrackerAnswerSet.impactLikertAnswers) {
                            for (const impactLikertAnswer of answer.impactTrackerAnswerSet.impactLikertAnswers) {
                                contextParts.push(`Statement: ${impactLikertAnswer.statement}`);
                                contextParts.push(`Left Label: ${impactLikertAnswer.leftLabel}`);
                                contextParts.push(`Right Label: ${impactLikertAnswer.rightLabel}`);
                            }
                        }
                    } else if (answer.answerType === "file" && answer.taskAnswerFile) {
                        contextParts.push(`File name: ${answer.taskAnswerFile.name}`);
                    }
                }
            }
        }
    }

    return contextParts.join("\n\n");
}

/**
 * Builds an array of source texts to translate from a submitted task
 *
 * @param submittedTask The submitted task containing thread items
 * @returns An array of unique text strings to be translated
 */
export function buildThreadSourceTexts(submittedTask: ISubmittedTaskWithThread): string[] {
    const out = new Set<string>();

    if (submittedTask?.thread) {
        for (const item of submittedTask.thread) {
            if (item.type === "response" && item.content) {
                out.add(item.content);
            } else if (item.type === "feedback" && item.content) {
                out.add(item.content);
            } else if (item.type === "answer" && item.taskAnswers) {
                for (const answer of item.taskAnswers) {
                    if (answer.questionContent) {
                        out.add(answer.questionContent);
                    }
                    if (answer.answerType === "textbox" && answer.answerContent) {
                        out.add(answer.answerContent);
                    } else if (answer.answerType === "select-in-image" && answer.imageSelectionAnswers) {
                        for (const imageSelectionAnswer of answer.imageSelectionAnswers) {
                            if (imageSelectionAnswer.answer) {
                                out.add(imageSelectionAnswer.answer);
                            }
                            if (imageSelectionAnswer.explanation) {
                                out.add(imageSelectionAnswer.explanation);
                            }
                        }
                    } else if (answer.answerType === "likert" && answer.taskLikertAnswers) {
                        for (const likertAnswer of answer.taskLikertAnswers) {
                            if (likertAnswer.statement) {
                                out.add(likertAnswer.statement);
                            }
                            if (likertAnswer.leftLabel) {
                                out.add(likertAnswer.leftLabel);
                            }
                            if (likertAnswer.rightLabel) {
                                out.add(likertAnswer.rightLabel);
                            }
                        }
                    } else if (answer.answerType === "checkbox" && answer.checkboxAnswers) {
                        for (const checkboxAnswer of answer.checkboxAnswers) {
                            if (checkboxAnswer.content) {
                                out.add(checkboxAnswer.content);
                            }
                        }
                    } else if (answer.answerType === "impact-tracker" && answer.impactTrackerAnswerSet) {
                        if (answer.impactTrackerAnswerSet.impactLikertAnswers) {
                            for (const impactLikertAnswer of answer.impactTrackerAnswerSet.impactLikertAnswers) {
                                if (impactLikertAnswer.statement) {
                                    out.add(impactLikertAnswer.statement);
                                }
                                if (impactLikertAnswer.leftLabel) {
                                    out.add(impactLikertAnswer.leftLabel);
                                }
                                if (impactLikertAnswer.rightLabel) {
                                    out.add(impactLikertAnswer.rightLabel);
                                }
                            }
                        }
                    } else if (answer.answerType === "file" && answer.taskAnswerFile) {
                        if (answer.taskAnswerFile.name) {
                            out.add(answer.taskAnswerFile.name);
                        }
                    }
                }
            }
        }
    }

    return Array.from(out);
}

export interface ISubmittedTask {
    shortTitle: string;
    title?: string | null;
    objectives?: string | null;
    media?: {
        name: string;
    }[];
    submittedTaskVariant?: {
        variantTitle?: string | null;
        description?: string | null;
        hoursQuestions?: string | null;
        taskQuestions?: {
            questionContent: string;
        }[];
    } | null;
}

/**
 * Builds a context string for a submitted task
 *
 * @param submittedTask The submitted task
 * @returns A formatted context string with task information
 */
export function buildTaskTranslationContext(submittedTask: ISubmittedTask): string {
    const contextParts: string[] = [];

    if (submittedTask?.shortTitle) {
        contextParts.push(`Short Title: ${submittedTask.shortTitle}`);
    }

    if (submittedTask?.title) {
        contextParts.push(`Title: ${submittedTask.title}`);
    }

    if (submittedTask?.objectives) {
        contextParts.push(`Objectives: ${submittedTask.objectives}`);
    }

    if (submittedTask?.media) {
        for (const item of submittedTask.media) {
            if (item.name) {
                contextParts.push(`Media Name: ${item.name}`);
            }
        }
    }

    const variant = submittedTask?.submittedTaskVariant;

    if (variant?.variantTitle) {
        contextParts.push(`Variant Title: ${variant.variantTitle}`);
    }

    if (variant?.description) {
        contextParts.push(variant.description);
    }

    if (variant?.hoursQuestions) {
        contextParts.push(variant.hoursQuestions);
    }

    if (variant?.taskQuestions) {
        for (const question of variant.taskQuestions) {
            if (question.questionContent) {
                contextParts.push(question.questionContent);
            }
        }
    }

    return contextParts.join("\n\n");
}

/**
 * Builds an array of source texts to translate from a submitted task
 *
 * @param submittedTask The submitted task
 * @returns An array of unique text strings to be translated
 */
export function buildTaskSourceTexts(submittedTask: ISubmittedTask): string[] {
    const out = new Set<string>();

    if (submittedTask?.shortTitle) {
        out.add(submittedTask.shortTitle);
    }

    if (submittedTask?.title) {
        out.add(submittedTask.title);
    }

    if (submittedTask?.objectives) {
        out.add(submittedTask.objectives);
    }

    if (submittedTask?.media) {
        for (const item of submittedTask.media) {
            if (item.name) {
                out.add(item.name);
            }
        }
    }

    const variant = submittedTask?.submittedTaskVariant;

    if (variant?.variantTitle) {
        out.add(variant.variantTitle);
    }

    if (variant?.description) {
        out.add(variant.description);
    }

    if (variant?.hoursQuestions) {
        out.add(variant.hoursQuestions);
    }

    if (variant?.taskQuestions) {
        for (const question of variant.taskQuestions) {
            if (question.questionContent) {
                out.add(question.questionContent);
            }
        }
    }

    return Array.from(out);
}

export interface IMessage {
    subject: string;
    content: string;
    automated: boolean;
}

/**
 * Builds a context string for message translation
 *
 * @param message The message object containing subject and content
 * @returns A formatted context string with message information
 */
export function buildMessageTranslationContext(message: IMessage): string {
    return `${message.subject}\n\n${message.content}`;
}

/**
 * Builds an array of source texts to translate from a message
 *
 * @param message The message object containing subject and content
 * @returns An array of text strings to be translated
 */
export function buildMessageSourceTexts(message: IMessage): string[] {
    return [message.subject, message.content];
}

export interface ICollectFeedbackAnswerSet {
    collectFeedbackAnswers: {
        nr: number;
        type: CollectFeedbackQuestionType;
        question: string;
        translation: string | null;
        leftLabel: string | null;
        rightLabel: string | null;
        leftTranslation: string | null;
        rightTranslation: string | null;
        textAnswer: string | null;
        likertAnswer: number | null;
    }[];
}

/**
 * Builds a context string for collect feedback translation
 *
 * @param answerSet The collect feedback answer set containing questions and answers
 * @returns A formatted context string with questions and answers
 */
export function buildCollectFeedbackTranslationContext(answerSet: ICollectFeedbackAnswerSet): string {
    const contextParts: string[] = [];

    for (const answer of answerSet.collectFeedbackAnswers) {
        let questionPart = `Question ${answer.nr}: ${answer.question}`;

        if (answer.translation) {
            questionPart += `\nTranslation: ${answer.translation}`;
        }

        if (answer.type === "text" && answer.textAnswer) {
            contextParts.push(`${questionPart}\nAnswer: ${answer.textAnswer}`);
        } else if (answer.type === "likert" && answer.likertAnswer !== null) {
            const leftLabel = answer.leftLabel || "";
            const rightLabel = answer.rightLabel || "";

            let likertInfo = `${questionPart}\nLikert scale (${leftLabel}`;

            if (answer.leftTranslation) {
                likertInfo += ` [${answer.leftTranslation}]`;
            }

            likertInfo += ` - ${rightLabel}`;

            if (answer.rightTranslation) {
                likertInfo += ` [${answer.rightTranslation}]`;
            }

            likertInfo += `): ${answer.likertAnswer}`;

            contextParts.push(likertInfo);
        }
    }

    return contextParts.join("\n\n");
}

/**
 * Builds an array of source texts to translate from a collect feedback answer set
 *
 * @param answerSet The collect feedback answer set containing questions and answers
 * @returns An array of unique text strings to be translated
 */
export function buildCollectFeedbackSourceTexts(answerSet: ICollectFeedbackAnswerSet): string[] {
    const out = new Set<string>();

    for (const answer of answerSet.collectFeedbackAnswers) {
        if (answer.type === "text" && answer.textAnswer) {
            out.add(answer.textAnswer);
        }
        if (answer.translation) {
            out.add(answer.translation);
        }
        if (answer.leftTranslation) {
            out.add(answer.leftTranslation);
        }
        if (answer.rightTranslation) {
            out.add(answer.rightTranslation);
        }
    }

    return Array.from(out);
}

/**
 * Utility function to look up AI translations
 *
 * @param content The original content to translate
 * @param translations Optional record mapping original content to translations
 * @returns The translated content if available, otherwise the original content
 */
export function lookupAiTranslation(content: string | null | undefined, translations?: Record<string, string>): string {
    if (!content) {
        return "";
    }

    // Trim the content when looking up in translations to handle whitespace differences
    const trimmedContent = content.trim();
    return translations?.[trimmedContent] ?? content;
}

/**
 * Gets the target language for translation based on the current user's locale
 *
 * @returns The user's locale if available, otherwise defaults to "en"
 */
export function getTargetLanguage(): Language {
    const jsData = loadJSData();
    return jsData.current_person?.locale || "en";
}
